import { useEffect, useState } from 'react'
import { Loader2, Save } from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { useSettings, useUpdateSettings } from '@/hooks/use-settings'
import { OrgLogoCard } from '@/components/shared/org-logo-card'
import { api } from '@/lib/api-client'
import { useAuthStore } from '@/stores/auth-store'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { COMPANY_FIELDS } from './settings-utils'

/** Logo riêng của tổ chức — owner/admin tự đặt; đổi xong cập nhật luôn thanh menu. */
function LogoToChuc() {
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const qc = useQueryClient()
  const canEdit = user?.role === 'owner' || user?.role === 'admin'
  const q = useQuery<{ logoUrl: string | null }>({
    queryKey: ['settings', 'logo'],
    queryFn: async () => (await api.get('/settings/logo')).data,
  })
  const [dangLuu, setDangLuu] = useState(false)
  const capNhat = (logoUrl: string | null) => {
    qc.setQueryData(['settings', 'logo'], { logoUrl })
    if (user?.org) setUser({ ...user, org: { ...user.org, logoUrl } })
  }
  return (
    <OrgLogoCard
      logoUrl={q.data?.logoUrl}
      canEdit={!!canEdit}
      dangLuu={dangLuu}
      onUpload={async (dataUrl) => { setDangLuu(true); try { const r = await api.put<{ logoUrl: string | null }>('/settings/logo', { dataUrl }); capNhat(r.data.logoUrl) } finally { setDangLuu(false) } }}
      onClear={async () => { setDangLuu(true); try { await api.delete('/settings/logo'); capNhat(null) } finally { setDangLuu(false) } }}
    />
  )
}

export function CompanyTab() {
  const { data, isLoading, isError } = useSettings()
  const update = useUpdateSettings()
  const [form, setForm] = useState<Record<string, string>>({})

  // Nạp giá trị hiện có vào form khi có dữ liệu.
  useEffect(() => {
    if (!data) return
    const next: Record<string, string> = {}
    for (const f of COMPANY_FIELDS) next[f.key] = data.settings[f.key] ?? ''
    setForm(next)
  }, [data])

  if (isLoading) return <Loading label="Đang tải thông tin công ty..." />
  if (isError || !data) return <ErrorState />

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const entries = COMPANY_FIELDS.map((f) => ({ key: f.key, value: form[f.key] ?? '' }))
    try {
      await update.mutateAsync(entries)
      toast.success('Đã lưu thông tin công ty')
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <div className="space-y-4">
    <LogoToChuc />
    <Card>
      <CardHeader>
        <CardTitle>Thông tin công ty</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {COMPANY_FIELDS.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={f.key}>{f.label}</Label>
                <Input
                  id={f.key}
                  type={f.type || 'text'}
                  placeholder={f.placeholder}
                  value={form[f.key] ?? ''}
                  onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? <Loader2 className="animate-spin" /> : <Save />}
              Lưu thay đổi
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
    </div>
  )
}
